import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
} from "lucide-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useSession } from "@/hooks/useSessions";
import { useSessionVideos } from "@/hooks/useSessionVideos";
import { useSessionRecordings } from "@/hooks/useSessionRecordings";
import { useProgressiveDetailWindow } from "@/hooks/useProgressiveDetailWindow";
import { useVideoResumePosition } from "@/hooks/useVideoResumePosition";
import { useNotesUi } from "@/hooks/useNotesUi";
import { useRecordingDeepLink } from "@/hooks/useRecordingDeepLink";
import { useDebouncedSetting } from "@/hooks/useDebouncedSetting";
import {
  useAnalyticsKeyboardShortcuts,
  type ChartControls,
  type VideoControls,
} from "@/hooks/useAnalyticsKeyboardShortcuts";
import { useAuth } from "@/hooks/useAuth";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import MobileAnalyticsLayout from "@/components/analytics/MobileAnalyticsLayout";
import AnalyticsHeader from "@/components/analytics/AnalyticsHeader";
import NotesSidePanel from "@/components/analytics/NotesSidePanel";
import { TelemetryNoteModal } from "@/components/telemetry/TelemetryNoteModal";
import VideoOnlyAnalyticsLayout from "@/components/video/VideoOnlyAnalyticsLayout";
import { useVideoTelemetrySync } from "@/hooks/useVideoTelemetrySync";
import {
  useTelemetryLaps,
  useTelemetryManager,
  useTelemetryVisualization,
} from "@/hooks/useTelemetry";
import { useTelemetryTimestamp } from "@/hooks/useTelemetryTimestamp";
import { useAnalyticsSettings } from "@/hooks/useAnalyticsSettings";
import PageLoading from "@/components/common/PageLoading";
import PageErrorState from "@/components/common/PageErrorState";
import { useTelemetryNotesManager } from "@/hooks/useTelemetryNotes";
import { useTrackLayout } from "@/hooks/useTrackLayout";
import { useVehiclePosition } from "@/hooks/useVehiclePosition";
import ChannelsPanel from "@/components/analytics/ChannelsPanel";
import TrackMapPanel from "@/components/analytics/TrackMapPanel";
import ChartPanel from "@/components/analytics/ChartPanel";
import VideoPanel from "@/components/analytics/VideoPanel";
import type { TrackMapControls } from "@/components/tracks/useTrackMapZoom";
import { shouldShowVideoOnlyLayout } from "@/utils/analyticsLayout";
import { handleApiError, type ApiError } from "@/utils/errorHandler";
import { getVideoTimelineAnchor } from "@/utils/videoTimeline";
import { isTelemetryNotFoundError } from "@/hooks/useTelemetry";
import { reconcileChannelColorIndices } from "@/components/telemetry/channelColors";
import {
  clampToTimelineRange,
  getSharedTimelineRange,
  getTelemetryDataRange,
  normalizeTimelineRange,
} from "@/utils/sharedTimeline";
import {
  buildVideoSessionTimeline,
  getVideoSessionClipAtTime,
} from "@/utils/videoSessionTimeline";
import type { SessionLapMarker } from "@/utils/lapTimeline";

export default function SessionAnalyticsPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isMobileLayout = useMediaQuery("(max-width: 1023px)");
  const { videos, error: videosError } = useSessionVideos(sessionId!);
  const [chartControls, setChartControls] = useState<ChartControls | null>(
    null,
  );
  const [mapControls, setMapControls] = useState<TrackMapControls | null>(null);
  const [videoControls, setVideoControls] = useState<VideoControls | null>(
    null,
  );
  const [videoDurations, setVideoDurations] = useState<Record<string, number>>(
    {},
  );

  // Chart hover state for position tracking
  const [hoverTimestamp, setHoverTimestamp] = useState<number | null>(null);

  // Persistent analytics settings
  const { settings, updateSetting, updateSettings, persistPatch } =
    useAnalyticsSettings(sessionId!);

  // Telemetry data management (hoisted above the per-session reset block so
  // the progressive-detail hook's resetDetail is in scope for it).
  const telemetryManager = useTelemetryManager(sessionId!);
  const visualizationData = useTelemetryVisualization(
    sessionId!,
    settings.selectedChannels,
    undefined, // timeRange
  );
  const telemetryDataRange = useMemo(
    () => getTelemetryDataRange(visualizationData.allData),
    [visualizationData.allData],
  );

  // Progressive chart resolution (see useProgressiveDetailWindow).
  const { chartData, resetDetail } = useProgressiveDetailWindow(
    sessionId!,
    settings.chartViewport,
    telemetryDataRange,
    visualizationData.allData,
    settings.selectedChannels,
  );

  // Track whether we've initialized from settings to prevent race conditions.
  // Keyed by sessionId: the router renders the same element for
  // /sessions/a/analytics -> /sessions/b/analytics, so latched refs would
  // otherwise skip the restore for every session after the first.
  const initializedForSessionRef = useRef<string | undefined>(undefined);
  const syncOffsetInitializedRef = useRef(false);
  const selectedVideoIdInitializedRef = useRef(false);
  if (initializedForSessionRef.current !== sessionId) {
    initializedForSessionRef.current = sessionId;
    syncOffsetInitializedRef.current = false;
    selectedVideoIdInitializedRef.current = false;
    resetDetail();
  }

  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError,
  } = useSession(sessionId!);
  const isSessionOwner = !!user && !!session && session.userId === user.id;

  // Recordings fetch/poll/refresh lifecycle (see useSessionRecordings).
  const { recordings, refreshRecordingPlayback } =
    useSessionRecordings(session);

  // Telemetry notes management
  const notesManager = useTelemetryNotesManager(sessionId!);

  // Notes panel/modal UI state (see useNotesUi).
  const {
    showNotesPanel,
    setShowNotesPanel,
    isNoteModalOpen,
    notesEnabled,
    setNotesEnabled,
    handleNoteCreate,
    handleNoteClick,
    handleModalClose,
    handleCloseNotesPanel,
  } = useNotesUi(notesManager);

  // Telemetry-derived laps for the video timeline's start/finish markers.
  // Shares the react-query cache with the chart's lap bars, so both always
  // render the same laps.
  const { data: telemetryLapsResponse } = useTelemetryLaps(
    sessionId!,
    !!sessionId,
  );
  const timelineLaps = telemetryLapsResponse?.data?.laps ?? [];

  // Video-telemetry synchronization
  const [syncState, syncActions] = useVideoTelemetrySync(
    settings.videoCurrentTime,
  );
  const setSyncCurrentTime = syncActions.setCurrentTime;
  const currentTimeRef = useRef(syncState.currentTime);

  useEffect(() => {
    currentTimeRef.current = syncState.currentTime;
  }, [syncState.currentTime]);

  // Video-position resume persistence (see useVideoResumePosition).
  const { recordTime: recordVideoTime, flush: flushVideoCurrentTime } =
    useVideoResumePosition(settings.videoCurrentTime, persistPatch);

  // Track layout data
  const {
    data: trackLayout,
    isLoading: trackLayoutLoading,
    error: trackLayoutError,
  } = useTrackLayout(session?.event?.track?.id || "");

  // Vehicle position data - use hover timestamp when hovering, otherwise use playback time
  const positionTimestamp =
    hoverTimestamp !== null
      ? hoverTimestamp
      : syncState.currentTime + syncState.syncOffset;

  const { position: vehiclePosition, positionTrail } = useVehiclePosition(
    sessionId!,
    positionTimestamp,
    telemetryManager.hasTelemetryData && !!trackLayout,
  );

  // Extract telemetry recording timestamp with fallback to session.conductedAt
  const telemetryTimestamp = useTelemetryTimestamp(
    sessionId!,
    session?.conductedAt,
  );

  // Memoize available videos (must be at top level)
  const availableVideos = useMemo(
    () => videos.filter((v) => v.uploadStatus === "completed"),
    [videos],
  );
  const candidateVideoTimeline = useMemo(
    () =>
      buildVideoSessionTimeline(
        availableVideos,
        videoDurations,
        session?.conductedAt,
      ),
    [availableVideos, session?.conductedAt, videoDurations],
  );
  const videoTimeline =
    candidateVideoTimeline?.clips.length === availableVideos.length
      ? candidateVideoTimeline
      : null;

  // Recording deep link (?recording=<id>, see useRecordingDeepLink).
  const requestedVideoId = useRecordingDeepLink(
    sessionId,
    recordings,
    availableVideos,
    syncState.selectedVideoId,
    searchParams,
  );

  const activeVideoClip = getVideoSessionClipAtTime(
    videoTimeline,
    syncState.currentTime,
    syncState.selectedVideoId,
  );
  const selectedVideo =
    activeVideoClip?.video ??
    availableVideos.find((video) => video.id === syncState.selectedVideoId) ??
    availableVideos[0] ??
    null;
  const selectedVideoDuration =
    (selectedVideo ? videoDurations[selectedVideo.id] : undefined) ??
    selectedVideo?.duration ??
    null;

  const summaryTimeRange = telemetryManager.summary?.timeRange;
  const metadataTimeRange = telemetryManager.metadata?.timeRange;
  const telemetryTimelineRange = useMemo(
    () =>
      normalizeTimelineRange(summaryTimeRange?.start, summaryTimeRange?.end) ??
      normalizeTimelineRange(
        metadataTimeRange?.start,
        metadataTimeRange?.end,
      ) ??
      telemetryDataRange,
    [
      summaryTimeRange?.start,
      summaryTimeRange?.end,
      metadataTimeRange?.start,
      metadataTimeRange?.end,
      telemetryDataRange,
    ],
  );
  const sharedTimelineRange = useMemo(
    () =>
      getSharedTimelineRange({
        videoDurationSeconds: selectedVideoDuration,
        videoRange: videoTimeline?.range,
        telemetryRange: telemetryTimelineRange,
        syncOffsetSeconds: syncState.syncOffset,
      }),
    [
      selectedVideoDuration,
      telemetryTimelineRange,
      syncState.syncOffset,
      videoTimeline?.range,
    ],
  );
  const clampSharedTimelineTime = useCallback(
    (time: number) => clampToTimelineRange(time, sharedTimelineRange),
    [sharedTimelineRange],
  );
  const videoTimelineAnchor = getVideoTimelineAnchor(
    videoTimeline?.anchorTimestamp ?? selectedVideo?.recordedAt,
    session?.conductedAt,
  );
  const videoTimelineViewport = useMemo(() => {
    if (!settings.chartViewport) {
      return sharedTimelineRange;
    }

    return normalizeTimelineRange(
      settings.chartViewport.startTime - syncState.syncOffset,
      settings.chartViewport.endTime - syncState.syncOffset,
    );
  }, [settings.chartViewport, sharedTimelineRange, syncState.syncOffset]);
  const telemetryFetchErrors = [
    telemetryManager.metadataError,
    telemetryManager.summaryError,
    visualizationData.error,
  ];
  const hasTelemetryNotFoundError = telemetryFetchErrors.some(
    (error) => isTelemetryNotFoundError(error),
  );
  const hasRealTelemetryError = telemetryFetchErrors.some(
    (error) => error && !isTelemetryNotFoundError(error),
  );
  const telemetryMissing =
    hasTelemetryNotFoundError && !hasRealTelemetryError;
  const showVideoOnlyLayout = shouldShowVideoOnlyLayout({
    availableVideoCount: availableVideos.length,
    hasTelemetryData: telemetryManager.hasTelemetryData,
    telemetryMissing,
    isLoading: visualizationData.isLoading,
  });

  // Memoize callback (must be at top level)
  const handleVideoTimeUpdate = useCallback(
    (time: number) => {
      // The video time represents the actual video playback position
      const clampedTime = clampSharedTimelineTime(time);
      syncActions.setCurrentTime(clampedTime, "video");
      // Persist video current time for resuming playback — kept in a ref and
      // flushed on leave (see useVideoResumePosition); a settings update per
      // timeupdate starved React Router's transition-based back navigation
      // (issue #1151).
      recordVideoTime(clampedTime);
    },
    [clampSharedTimelineTime, recordVideoTime, syncActions],
  );

  const handleDurationChange = useCallback(
    (videoId: string, duration: number) => {
      setVideoDurations((current) => ({
        ...current,
        [videoId]: duration,
      }));
    },
    [],
  );

  // Handle channel selection changes
  const handleChannelSelectionChange = useCallback(
    (channels: string[]) => {
      updateSettings({
        selectedChannels: channels,
        selectedChannelColorIndices: reconcileChannelColorIndices(
          settings.selectedChannelColorIndices,
          settings.selectedChannels,
          channels,
        ),
      });
    },
    [
      settings.selectedChannelColorIndices,
      settings.selectedChannels,
      updateSettings,
    ],
  );

  // Memoize chart callback functions to prevent infinite re-renders
  const handleTelemetryTimeUpdate = useCallback(
    (time: number) => {
      // When user clicks on telemetry, convert back to video time
      syncActions.setCurrentTime(
        clampSharedTimelineTime(time - syncState.syncOffset),
        "telemetry",
      );
    },
    [clampSharedTimelineTime, syncActions, syncState.syncOffset],
  );

  const handleVideoTimelineTimeUpdate = useCallback(
    (time: number) => {
      syncActions.setCurrentTime(
        clampSharedTimelineTime(time),
        "telemetry",
      );
    },
    [clampSharedTimelineTime, syncActions],
  );

  // Selecting a lap marker seeks the synced player to that crossing.
  const handleLapMarkerClick = useCallback(
    (marker: SessionLapMarker) => {
      handleVideoTimelineTimeUpdate(marker.sessionTime);
    },
    [handleVideoTimelineTimeUpdate],
  );

  const handleAutoCenterChange = useCallback(
    (value: boolean) => updateSetting("autoCenter", value),
    [updateSetting],
  );

  const handleShowGridChange = useCallback(
    (value: boolean) => updateSetting("showGrid", value),
    [updateSetting],
  );

  const handleChartHeightChange = useCallback(
    (value: number) => updateSetting("chartHeight", value),
    [updateSetting],
  );

  const handleChartViewportChange = useCallback(
    (value: { startTime: number; endTime: number } | null) =>
      updateSetting("chartViewport", value),
    [updateSetting],
  );

  // Handle chart hover for position tracking
  const handleChartHoverTimeUpdate = useCallback((timestamp: number | null) => {
    setHoverTimestamp(timestamp);
  }, []);

  const hasUsableTimeline = sharedTimelineRange !== null;
  const handleAddNoteAtCurrentTime = useCallback(() => {
    if (!hasUsableTimeline) {
      return;
    }
    handleNoteCreate(syncState.currentTime + syncState.syncOffset);
  }, [hasUsableTimeline, handleNoteCreate, syncState.currentTime, syncState.syncOffset]);

  // Initialize sync offset from settings (only once when settings are first loaded)
  useEffect(() => {
    if (!syncOffsetInitializedRef.current && settings.syncOffset !== undefined) {
      syncActions.setSyncOffset(settings.syncOffset);
      syncOffsetInitializedRef.current = true;
    }
  }, [settings.syncOffset, syncActions]);

  // Notes in video-only sessions anchor to the video timeline directly; the
  // sync offset is only meaningful when telemetry data exists. isTelemetryMissing
  // is definitive (404), so offsets survive while telemetry is still loading.
  useEffect(() => {
    if (telemetryManager.isTelemetryMissing && syncState.syncOffset !== 0) {
      syncActions.setSyncOffset(0);
    }
  }, [telemetryManager.isTelemetryMissing, syncState.syncOffset, syncActions]);

  // Persist sync offset changes (debounced but responsive)
  useDebouncedSetting(syncState.syncOffset, "syncOffset", updateSetting);

  // Initialize selected video from settings (only once when settings are first loaded)
  useEffect(() => {
    if (!selectedVideoIdInitializedRef.current && settings.selectedVideoId) {
      syncActions.setSelectedVideoId(settings.selectedVideoId);
      selectedVideoIdInitializedRef.current = true;
    }
  }, [settings.selectedVideoId, syncActions]);

  // Persist selected video changes (debounced but responsive)
  useDebouncedSetting(
    syncState.selectedVideoId,
    "selectedVideoId",
    updateSetting,
  );

  useEffect(() => {
    const clampedTime = clampSharedTimelineTime(syncState.currentTime);

    if (Math.abs(clampedTime - syncState.currentTime) > 0.01) {
      syncActions.syncToTime(clampedTime);
    }
  }, [clampSharedTimelineTime, syncActions, syncState.currentTime]);

  // Fetch videos for synchronization
  useEffect(() => {
    if (videosError) {
      handleApiError(
        videosError as ApiError,
        t("pages:sessions.analytics.videosLoadError"),
      );
    }
  }, [videosError, t]);

  const seekSharedTimeline = useCallback(
    (deltaSeconds: number) => {
      setSyncCurrentTime(
        clampSharedTimelineTime(currentTimeRef.current + deltaSeconds),
        "keyboard",
      );
    },
    [clampSharedTimelineTime, setSyncCurrentTime],
  );

  useAnalyticsKeyboardShortcuts({
    chartControls,
    videoControls,
    seekSharedTimeline,
    chartHeight: settings.chartHeight,
    onChartHeightChange: handleChartHeightChange,
    showNotesPanel,
    isNoteModalOpen,
    onCloseNotesPanel: handleCloseNotesPanel,
    onCloseNoteModal: handleModalClose,
  });

  if (!sessionId) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-600">{t("pages:sessions.analytics.invalidSessionId")}</p>
        </div>
      </div>
    );
  }

  if (sessionLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <PageLoading />
      </div>
    );
  }

  if (sessionError || !session) {
    return (
      <div className="h-screen flex items-center justify-center">
        <PageErrorState
          icon={AlertCircle}
          className=""
          message={t("pages:sessions.analytics.failedToLoadSession")}
          details={t("pages:sessions.analytics.noAccessMessage")}
          retryLabel={t("pages:sessions.analytics.backToSessions")}
          onRetry={() => navigate("/sessions")}
        />
      </div>
    );
  }

  return (
 <div className="dvh-fill flex min-h-0 flex-col overflow-hidden lg:min-h-[600px]">
      <AnalyticsHeader
        sessionId={sessionId}
        sessionTitle={session.title}
        isMobileLayout={isMobileLayout}
        hasTelemetryMetadata={!!telemetryManager.metadata}
        totalDataPoints={telemetryManager.summary?.totalDataPoints ?? 0}
        selectedChannelCount={settings.selectedChannels.length}
        laps={timelineLaps}
        currentTime={syncState.currentTime + syncState.syncOffset}
        showNotesControls={
          telemetryManager.hasTelemetryData || availableVideos.length > 0
        }
        noteCount={notesManager.notes.length}
        showNotesPanel={showNotesPanel}
        onToggleNotesPanel={() => setShowNotesPanel(!showNotesPanel)}
        onAddNoteAtCurrentTime={handleAddNoteAtCurrentTime}
        hasUsableTimeline={hasUsableTimeline}
        onBack={() => {
          // Pause first: the click-driven navigation runs as a React
          // Router transition, and a playing video's timeupdate stream
          // could starve it exactly like a browser back navigation
          // (issue #1151).
          videoControls?.pause();
          flushVideoCurrentTime();
          navigate(`/sessions/${sessionId}`);
        }}
      />

      {/* Main Content - Resizable Layout */}
      <div className="flex-1 bg-[#0d0d14] overflow-hidden min-h-0">
        {showVideoOnlyLayout ? (
          <VideoOnlyAnalyticsLayout
            key={sessionId}
            videos={availableVideos}
            recordings={recordings}
            currentTime={syncState.currentTime}
            onVideoTimeUpdate={handleVideoTimeUpdate}
            onVideoSelect={syncActions.setSelectedVideoId}
            initialVideoId={settings.selectedVideoId ?? undefined}
            requestedVideoId={requestedVideoId}
            onDurationChange={handleDurationChange}
            onGetVideoControls={setVideoControls}
            volume={settings.videoVolume}
            onVolumeChange={(value) => updateSetting("videoVolume", value)}
            onRefreshRecordingPlayback={refreshRecordingPlayback}
            anchorTimestamp={videoTimelineAnchor.timestamp}
            isUsingSessionFallback={videoTimelineAnchor.isFallback}
            durationSeconds={selectedVideoDuration}
            timeline={videoTimeline}
            notes={notesManager.notes}
            onNoteClick={handleNoteClick}
            onNoteCreate={handleNoteCreate}
            notesEnabled={notesEnabled}
            onTimelineTimeChange={handleVideoTimelineTimeUpdate}
          />
        ) : isMobileLayout ? (
          <MobileAnalyticsLayout
            sessionId={sessionId}
            videos={availableVideos}
            recordings={recordings}
            currentTime={syncState.currentTime}
            onVideoTimeUpdate={handleVideoTimeUpdate}
            onVideoSelect={syncActions.setSelectedVideoId}
            initialVideoId={settings.selectedVideoId ?? undefined}
            requestedVideoId={requestedVideoId}
            onDurationChange={handleDurationChange}
            onGetVideoControls={setVideoControls}
            volume={settings.videoVolume}
            onVolumeChange={(value) => updateSetting("videoVolume", value)}
            onRefreshRecordingPlayback={refreshRecordingPlayback}
            syncOffset={syncState.syncOffset}
            onSyncOffsetChange={
              telemetryManager.isTelemetryMissing
                ? undefined
                : syncActions.setSyncOffset
            }
            videoCreatedAt={videoTimelineAnchor.timestamp ?? undefined}
            telemetryStartTime={telemetryTimestamp.timestamp ?? null}
            telemetryTimestampSource={telemetryTimestamp.source}
            timeline={videoTimeline}
            timelineViewport={videoTimelineViewport}
            laps={timelineLaps}
            onLapClick={handleLapMarkerClick}
            onVideoTimelineTimeUpdate={handleVideoTimelineTimeUpdate}
            chartData={chartData}
            chartLoading={visualizationData.isLoading}
            chartError={
              visualizationData.error && !telemetryMissing
                ? (visualizationData.error as Error)
                : null
            }
            selectedChannels={settings.selectedChannels}
            channelColorIndices={settings.selectedChannelColorIndices}
            channelMetadata={visualizationData.allMetadata}
            syncCurrentTime={syncState.currentTime + syncState.syncOffset}
            onChartTimeUpdate={handleTelemetryTimeUpdate}
            onHoverTimeUpdate={handleChartHoverTimeUpdate}
            onGetChartControls={setChartControls}
            autoCenter={settings.autoCenter}
            onAutoCenterChange={handleAutoCenterChange}
            showGrid={settings.showGrid}
            onShowGridChange={handleShowGridChange}
            chartHeight={settings.chartHeight}
            onChartHeightChange={handleChartHeightChange}
            chartViewport={settings.chartViewport}
            onChartViewportChange={handleChartViewportChange}
            channels={telemetryManager.metadata?.channels || {}}
            onChannelSelectionChange={handleChannelSelectionChange}
            notes={notesManager.notes}
            onNoteClick={handleNoteClick}
            onNoteCreate={handleNoteCreate}
            onNoteUpdate={(noteId, newTimeOffset) =>
              notesManager.handleUpdateNote(noteId, {
                timeOffset: newTimeOffset,
              })
            }
            selectedNoteId={notesManager.selectedNote?.id ?? null}
            notesEnabled={notesEnabled}
            showNotesPanel={showNotesPanel}
            onCloseNotesPanel={handleCloseNotesPanel}
            onNotesTimeSeek={(time) => {
              syncActions.setCurrentTime(
                time - syncState.syncOffset,
                "telemetry",
              );
            }}
          />
        ) : (
        <PanelGroup
          direction="horizontal"
          className="h-full"
          onLayout={(sizes: number[]) => {
            if (sizes.length === 3) {
              updateSetting("leftPanelSize", sizes[0]);
              updateSetting("middlePanelSize", sizes[1]);
              updateSetting("rightPanelSize", sizes[2]);
            }
          }}
        >
          {/* Left Panel - Data Channels + Track Layout */}
          <Panel defaultSize={settings.leftPanelSize} minSize={15} maxSize={30}>
            <div className="flex flex-col h-full">
              <ChannelsPanel
                isLoading={telemetryManager.isLoading}
                error={telemetryManager.error}
                telemetryMissing={telemetryMissing}
                hasTelemetryData={telemetryManager.hasTelemetryData}
                channels={telemetryManager.metadata?.channels || {}}
                selectedChannels={settings.selectedChannels}
                channelColorIndices={settings.selectedChannelColorIndices}
                onSelectionChange={handleChannelSelectionChange}
              />
              <TrackMapPanel
                trackName={session.event.track?.name}
                trackType={session.event.track?.type}
                mapControls={mapControls}
                onGetMapControls={setMapControls}
                trackLayout={trackLayout}
                isLoading={trackLayoutLoading}
                error={trackLayoutError}
                vehiclePosition={vehiclePosition}
                positionTrail={positionTrail}
              />
            </div>
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle className="w-1 bg-[#1a1a2e] hover:bg-[#3b82f6] active:bg-[#3b82f6] transition-colors duration-150 cursor-col-resize" />

          {/* Middle Panel - Telemetry Chart */}
          <Panel defaultSize={settings.middlePanelSize} minSize={40}>
            <ChartPanel
              isLoading={visualizationData.isLoading}
              error={visualizationData.error}
              telemetryMissing={telemetryMissing}
              hasChannelsSelected={settings.selectedChannels.length > 0}
              hasData={visualizationData.allData.length > 0}
              chartData={chartData}
              selectedChannels={settings.selectedChannels}
              channelColorIndices={settings.selectedChannelColorIndices}
              channelMetadata={visualizationData.allMetadata}
              sessionId={sessionId}
              syncCurrentTime={syncState.currentTime + syncState.syncOffset}
              onTimeUpdate={handleTelemetryTimeUpdate}
              onHoverTimeUpdate={handleChartHoverTimeUpdate}
              onGetChartControls={setChartControls}
              autoCenter={settings.autoCenter}
              onAutoCenterChange={handleAutoCenterChange}
              showGrid={settings.showGrid}
              onShowGridChange={handleShowGridChange}
              chartHeight={settings.chartHeight}
              onChartHeightChange={handleChartHeightChange}
              chartViewport={settings.chartViewport}
              onChartViewportChange={handleChartViewportChange}
              notes={notesManager.notes}
              onNoteClick={handleNoteClick}
              onNoteCreate={handleNoteCreate}
              onNoteUpdate={(noteId, newTimeOffset) =>
                notesManager.handleUpdateNote(noteId, {
                  timeOffset: newTimeOffset,
                })
              }
              selectedNoteId={notesManager.selectedNote?.id}
              notesEnabled={notesEnabled}
              videoTimeline={videoTimeline}
              videoTimelineCurrentTime={syncState.currentTime}
              videoTimelineViewport={videoTimelineViewport}
              videoTimelineSyncOffset={syncState.syncOffset}
              onVideoTimelineTimeUpdate={handleVideoTimelineTimeUpdate}
              laps={timelineLaps}
              onLapClick={handleLapMarkerClick}
            />
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle className="w-1 bg-[#1a1a2e] hover:bg-[#3b82f6] active:bg-[#3b82f6] transition-colors duration-150 cursor-col-resize" />

          {/* Right Panel - Video */}
          <Panel defaultSize={settings.rightPanelSize} minSize={15} maxSize={40}>
            <VideoPanel
              sessionId={sessionId}
              videos={availableVideos}
              recordings={recordings}
              currentTime={syncState.currentTime}
              onTimeUpdate={handleVideoTimeUpdate}
              onVideoSelect={syncActions.setSelectedVideoId}
              initialVideoId={settings.selectedVideoId ?? undefined}
              requestedVideoId={requestedVideoId}
              onDurationChange={handleDurationChange}
              onGetVideoControls={setVideoControls}
              volume={settings.videoVolume}
              onVolumeChange={(value) => updateSetting("videoVolume", value)}
              syncOffset={syncState.syncOffset}
              onSyncOffsetChange={
                telemetryManager.isTelemetryMissing
                  ? undefined
                  : syncActions.setSyncOffset
              }
              videoCreatedAt={videoTimelineAnchor.timestamp ?? undefined}
              telemetryStartTime={telemetryTimestamp.timestamp}
              telemetryTimestampSource={telemetryTimestamp.source}
              timeline={videoTimeline}
              onRefreshRecordingPlayback={refreshRecordingPlayback}
              hasActiveClip={!!activeVideoClip}
            />
          </Panel>
        </PanelGroup>
        )}
      </div>

      {/* Notes Panel Overlay */}
      {showNotesPanel && !isMobileLayout && (
        <NotesSidePanel
          notes={notesManager.notes}
          selectedNoteId={notesManager.selectedNote?.id}
          notesEnabled={notesEnabled}
          onNotesEnabledChange={setNotesEnabled}
          onNoteClick={handleNoteClick}
          onTimeSeek={(time) => {
            syncActions.setCurrentTime(
              time - syncState.syncOffset,
              "telemetry",
            );
          }}
          onClose={() => setShowNotesPanel(false)}
        />
      )}

      {/* Telemetry Note Modal */}
      <TelemetryNoteModal
        isOpen={isNoteModalOpen}
        onClose={handleModalClose}
        note={notesManager.selectedNote}
        timeOffset={notesManager.pendingNoteTimeOffset ?? undefined}
        onCreateNote={notesManager.handleCreateNote}
        onUpdateNote={(noteData) =>
          notesManager.handleUpdateNote(notesManager.selectedNote!.id, noteData)
        }
        onDeleteNote={
          isSessionOwner
            ? () =>
                notesManager.handleDeleteNote(notesManager.selectedNote!.id)
            : undefined
        }
        isCreating={notesManager.isCreating}
        isUpdating={notesManager.isUpdating}
        isDeleting={notesManager.isDeleting}
        isCreateMode={notesManager.isCreatingNote}
        startInEditMode={
          !notesManager.isCreatingNote && !!notesManager.selectedNote
        }
      />
    </div>
  );
}
